import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

const NAV = [
  { label: "Home", id: "top" },
  { label: "Features", id: "features" },
  { label: "Creators", id: "creators" },
  { label: "Safety", id: "safety" },
  { label: "About", id: "about" },
  { label: "Support", to: "/support" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (id === "top") {
      if (location.pathname !== "/") navigate("/");
      else window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (location.pathname !== "/") {
      navigate("/#" + id);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const signIn = () =>
    toast("Sign in from the app", { description: "Signing in is available inside the IND RIL mobile app." });

  return (
    <motion.header
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="fixed inset-x-0 top-0 z-50 flex justify-center px-4 pt-3"
      data-testid="site-header"
    >
      <nav
        className={`flex w-full max-w-6xl items-center justify-between rounded-full px-4 py-2.5 transition-all duration-500 sm:px-6 ${
          scrolled ? "border border-white/10 bg-zinc-950/80 backdrop-blur-xl" : "border border-transparent"
        }`}
      >
        <button onClick={() => goSection("top")} data-testid="header-logo" aria-label="INDRIL home">
          <Logo markClass="h-8 w-8" />
        </button>

        <div className="hidden items-center gap-7 lg:flex">
          {NAV.map((n) =>
            n.to ? (
              <Link key={n.label} to={n.to} className="text-sm text-white/60 transition-colors hover:text-white" data-testid={`nav-${n.label.toLowerCase()}`}>
                {n.label}
              </Link>
            ) : (
              <button key={n.label} onClick={() => goSection(n.id)} className="text-sm text-white/60 transition-colors hover:text-white" data-testid={`nav-${n.label.toLowerCase()}`}>
                {n.label}
              </button>
            )
          )}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <button onClick={signIn} data-testid="header-signin" className="text-sm font-medium text-white/80 transition-colors hover:text-white">
            Sign In
          </button>
          <button onClick={() => goSection("download")} data-testid="header-get-app" className="rounded-full bg-white px-5 py-2 text-sm font-semibold text-zinc-950 transition-transform hover:scale-105">
            Get the App
          </button>
        </div>

        <button className="lg:hidden text-white" onClick={() => setOpen((v) => !v)} data-testid="header-menu-toggle" aria-label="Menu">
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute left-4 right-4 top-[4.5rem] rounded-3xl border border-white/10 bg-zinc-950/95 p-5 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-3">
              {NAV.map((n) =>
                n.to ? (
                  <Link key={n.label} to={n.to} onClick={() => setOpen(false)} className="rounded-xl px-3 py-2 text-white/80 hover:bg-white/5">
                    {n.label}
                  </Link>
                ) : (
                  <button key={n.label} onClick={() => goSection(n.id)} className="rounded-xl px-3 py-2 text-left text-white/80 hover:bg-white/5">
                    {n.label}
                  </button>
                )
              )}
              <div className="mt-2 flex gap-3">
                <button onClick={signIn} className="flex-1 rounded-full border border-white/15 px-4 py-2.5 text-sm text-white">Sign In</button>
                <button onClick={() => goSection("download")} className="flex-1 rounded-full bg-white px-4 py-2.5 text-sm font-semibold text-zinc-950">Get the App</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
